import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { pathFor, alternatePath, type Lang } from '../../router/paths';

// solid = Navigationsleiste dauerhaft weiss mit dunkler Schrift.
// Noetig auf Seiten ohne dunkles Hero-Bild (Impressum, Datenschutz, AGB, 404) -
// dort waere die transparente Variante weisse Schrift auf weissem Grund.
export default function Navigation({ solid = false }: { solid?: boolean }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState<'de' | 'en'>(i18n.language === 'en' ? 'en' : 'de');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const showSolid = solid || isScrolled;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const homePath = pathFor('home', lang);
  const langPath = alternatePath(location.pathname, lang === 'de' ? 'en' : 'de');

  const switchLang = () => {
    const next: Lang = lang === 'de' ? 'en' : 'de';
    i18n.changeLanguage(next);
    setLang(next);
    setIsMobileMenuOpen(false);
  };

  const scrollToSection = (id: string) => {
    if (location.pathname !== homePath) {
      navigate(homePath);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  // Cmd/Strg/Shift-Klick nicht abfangen - sonst laesst sich nichts mehr
  // in einem neuen Tab oeffnen.
  const isPlainClick = (e: React.MouseEvent) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);

  const onSectionClick = (e: React.MouseEvent, id: string) => {
    if (!isPlainClick(e)) return;
    e.preventDefault();
    scrollToSection(id);
  };

  const onPageClick = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setIsMobileMenuOpen(false);
  };

  const linkClass = `text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
    showSolid ? 'text-gray-700 hover:text-amber-700' : 'text-white hover:text-amber-200'
  }`;

  // Jeder Eintrag hat ein echtes Ziel (href), damit Suchmaschinen den Seiten
  // folgen koennen. section = Sprungmarke auf der Startseite, page = eigene Route.
  const navItems: { label: string; href: string; section?: string }[] = [
    { label: t('navRooms'),         href: pathFor('rooms', lang) },
    { label: t('navHotel'),         href: pathFor('hotel', lang) },
    { label: t('navLocation'),      href: `${homePath}#lage`,    section: 'lage' },
    { label: t('navContact'),       href: `${homePath}#kontakt`, section: 'kontakt' },
    { label: t('navAusflugsziele'), href: pathFor('ausflugsziele', lang) },
  ];

  const renderNavItem = ({ label, href, section }: { label: string; href: string; section?: string }, className: string) => (
    <Link
      key={label}
      to={href}
      className={className}
      onClick={section ? (e) => onSectionClick(e, section) : onPageClick}
    >
      {label}
    </Link>
  );

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${showSolid ? 'bg-white shadow-md' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-14 lg:h-20">

          <Link to={homePath} onClick={onPageClick} className="flex items-center" aria-label={t('hotelName')}>
            <img
              src="/images/brand/logo_hotel_alpenhof.png"
              alt="Hotel Alpenhof Gauting Logo"
              className="h-14 lg:h-24 w-auto"
              style={{ marginTop: '4px', marginBottom: '-8px' }}
            />
          </Link>

          {/* Desktop */}
          <div className="hidden lg:flex items-center space-x-7">
            {navItems.map((item) => renderNavItem(item, linkClass))}

            {/* Sprachauswahl - ebenfalls ein echter Link auf die andere Sprachfassung */}
            <Link
              to={langPath}
              onClick={switchLang}
              hrefLang={lang === 'de' ? 'en' : 'de'}
              className={`text-xs font-semibold tracking-widest border rounded px-2 py-1 transition-colors cursor-pointer ${
                showSolid
                  ? 'border-gray-300 text-gray-500 hover:border-amber-700 hover:text-amber-700'
                  : 'border-white/50 text-white/80 hover:border-white hover:text-white'
              }`}
            >
              {lang === 'de' ? 'EN' : 'DE'}
            </Link>

            <Link
              to={pathFor('buchen', lang)}
              onClick={onPageClick}
              className="bg-amber-700 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('navBook')}
            </Link>
          </div>

          {/* MOBILE: Hotel-Name zentriert zwischen Logo und Burger */}
          <span className={`lg:hidden text-xs font-semibold tracking-widest uppercase ${showSolid ? 'text-gray-500' : 'text-white/80'}`}>
            {t('hotelName')}
          </span>

          {/* Bleibt ein echter Knopf: klappt nur das Menue auf, fuehrt nirgendwohin. */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menü"
            aria-expanded={isMobileMenuOpen}
            className="lg:hidden cursor-pointer w-11 h-11 flex items-center justify-center -mr-2"
          >
            <i className={`ri-menu-line text-2xl ${showSolid ? 'text-gray-700' : 'text-white'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-6 py-4">
            {/* Sprachwechsel oben rechts */}
            <div className="flex justify-end pb-2">
              <Link
                to={langPath}
                onClick={switchLang}
                hrefLang={lang === 'de' ? 'en' : 'de'}
                aria-label={`Sprache wechseln zu ${lang === 'de' ? 'English' : 'Deutsch'}`}
                className="text-xs font-semibold tracking-widest uppercase text-amber-700 hover:text-amber-600 transition-colors cursor-pointer py-2 px-3 -mr-3"
              >
                {lang === 'de' ? 'English' : 'Deutsch'}
              </Link>
            </div>
            <div className="space-y-1">
              {navItems.map((item) =>
                renderNavItem(item, 'block w-full text-left text-sm font-medium text-gray-700 hover:text-amber-700 cursor-pointer py-3 min-h-11'),
              )}
              <Link
                to={pathFor('buchen', lang)}
                onClick={onPageClick}
                className="block w-full text-center bg-amber-700 text-white px-6 py-3 rounded-md text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer mt-3"
              >
                {t('navBook')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
